import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { SelectField } from "../..";
import type { IMultiSortItem, IMultiSortModel } from "../model/types";

type Props = { model: IMultiSortModel };

type DraftRow = IMultiSortItem & { id: string };

const makeId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id_${Math.random().toString(16).slice(2)}_${Date.now()}`;

function toggleDir(dir: "asc" | "desc"): "asc" | "desc" {
  return dir === "asc" ? "desc" : "asc";
}

function isEqual(a: IMultiSortItem[], b: IMultiSortItem[]) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].sortBy !== b[i].sortBy) return false;
    if (a[i].sortDirection !== b[i].sortDirection) return false;
  }

  return true;
}

function toDraftRows(value: IMultiSortItem[]): DraftRow[] {
  return value.map((v) => ({ ...v, id: makeId() }));
}

function stripDraft(rows: DraftRow[]): IMultiSortItem[] {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return rows.map(({ id: _id, ...rest }) => rest);
}

function SortRowItem({
  row,
  index,
  getRowOptions,
  onFieldChange,
  onToggleDirection,
  onRemove,
}: {
  row: DraftRow;
  index: number;
  options: IMultiSortModel["options"];
  getRowOptions: (rowIndex: number) => IMultiSortModel["options"];
  onFieldChange: (index: number, sortBy: string) => void;
  onToggleDirection: (index: number) => void;
  onRemove: (index: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as const;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        p: 2,
        bgcolor: isDragging ? "grey.50" : "background.paper",
        opacity: isDragging ? 0.85 : 1,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            aria-label="drag"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab" }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>

          <Typography sx={{ fontSize: 12, color: "grey.700" }}>
            Приоритет {index + 1}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <IconButton onClick={() => onRemove(index)} aria-label="remove">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SelectField
              fullWidth
              value={row.sortBy}
              options={getRowOptions(index)}
              onChange={(e) => onFieldChange(index, String(e.target.value))}
            />
          </Box>

          <IconButton
            onClick={() => onToggleDirection(index)}
            aria-label="toggle-direction"
          >
            {row.sortDirection === "asc" ? <NorthIcon /> : <SouthIcon />}
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function TableMultiSort({ model }: Props) {
  const { value, onChange, options } = model;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftRow[]>(toDraftRows(value));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (open) setDraft(toDraftRows(value));
  }, [open, value]);

  const usedFields = useMemo(
    () => new Set(draft.map((v) => v.sortBy)),
    [draft],
  );

  const availableOptions = useMemo(() => {
    return options.filter((o) => !usedFields.has(String(o.value)));
  }, [options, usedFields]);

  const getRowOptions = (rowIndex: number) => {
    const current = draft[rowIndex]?.sortBy;
    const used = new Set(draft.map((x) => x.sortBy));

    return options.filter((o) => {
      const v = String(o.value);

      return v === current || !used.has(v);
    });
  };

  const hasChanges = useMemo(() => {
    const plain = stripDraft(draft);

    return !isEqual(plain, value);
  }, [draft, value]);

  const addFirstAvailable = () => {
    const first = availableOptions[0];
    if (!first) return;

    setDraft((prev) => [
      ...prev,
      { id: makeId(), sortBy: String(first.value), sortDirection: "asc" },
    ]);
  };

  const updateField = (index: number, sortBy: string) => {
    setDraft((prev) => {
      const duplicate = prev.some((x, i) => i !== index && x.sortBy === sortBy);
      if (duplicate) return prev;

      return prev.map((item, i) => (i === index ? { ...item, sortBy } : item));
    });
  };

  const toggleDirection = (index: number) => {
    setDraft((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, sortDirection: toggleDir(item.sortDirection) }
          : item,
      ),
    );
  };

  const remove = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const reset = () => setDraft([]);

  const closeWithoutApply = () => setOpen(false);

  const applyAndClose = () => {
    if (hasChanges) onChange(stripDraft(draft));
    setOpen(false);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setDraft((prev) => {
      const oldIndex = prev.findIndex((x) => x.id === active.id);
      const newIndex = prev.findIndex((x) => x.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;

      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  return (
    <>
      <Button
        sx={{
          padding: (theme) => theme.spacing(2, 3),
        }}
        variant="text"
        onClick={() => setOpen(true)}
      >
        <SwapVertIcon />
        Сортировка
      </Button>

      <Drawer
        anchor="right"
        open={open}
        onClose={closeWithoutApply}
        PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}
      >
        <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Сортировка
          </Typography>

          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <IconButton onClick={closeWithoutApply} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 12, color: "grey.700", mb: 2 }}>
            Перетащите строки, чтобы изменить приоритет сортировки.
          </Typography>

          {draft.length === 0 ? (
            <Typography sx={{ color: "grey.700", mb: 2 }}>
              Добавьте поля, по которым нужно сортировать.
            </Typography>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={draft.map((x) => x.id)}
                strategy={verticalListSortingStrategy}
              >
                <Stack spacing={2} sx={{ mb: 3 }}>
                  {draft.map((row, index) => (
                    <SortRowItem
                      key={row.id}
                      row={row}
                      index={index}
                      options={options}
                      getRowOptions={getRowOptions}
                      onFieldChange={updateField}
                      onToggleDirection={toggleDirection}
                      onRemove={remove}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          )}

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={addFirstAvailable}
              disabled={availableOptions.length === 0}
            >
              Добавить
            </Button>

            <Button variant="text" onClick={reset} disabled={!draft.length}>
              Сбросить
            </Button>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="outlined"
              onClick={applyAndClose}
              disabled={!hasChanges}
            >
              Готово
            </Button>
          </Stack>

          {availableOptions.length === 0 && draft.length > 0 ? (
            <Typography sx={{ fontSize: 12, color: "grey.700" }}>
              Все доступные поля уже добавлены.
            </Typography>
          ) : null}
        </Box>
      </Drawer>
    </>
  );
}
